/** A report period: whole days `from`…`to` (inclusive), as the reporting API takes them (BB-123, BB-79). */
export interface Period {
  from: string;
  to: string;
}

export const PERIOD_PRESETS = [7, 30, 90] as const;
/** The reporting API refuses longer periods. */
export const MAX_PERIOD_DAYS = 366;
const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** Today in the employee's time zone, `YYYY-MM-DD`. */
export function today(now = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export const addDays = (day: string, days: number) => new Date(Date.parse(`${day}T00:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10);

/** The last `days` days up to and including `end`. */
export const lastDays = (days: number, end = today()): Period => ({ from: addDays(end, -(days - 1)), to: end });

/** Number of days in the period, both ends included; `null` for an unreadable date. */
export function periodDays({ from, to }: Period): number | null {
  if (!DAY.test(from) || !DAY.test(to) || Number.isNaN(Date.parse(from)) || Number.isNaN(Date.parse(to))) return null;
  return Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000) + 1;
}

/** Why the API would refuse the period (400), or `null` when it can be asked for. */
export function periodProblem(period: Period): string | null {
  const days = periodDays(period);
  if (days === null) return 'Pick both dates.';
  if (days < 1) return 'The start date must not be after the end date.';
  if (days > MAX_PERIOD_DAYS) return `A period can be at most ${MAX_PERIOD_DAYS} days long.`;
  return null;
}

export interface PeriodSearch {
  from?: string;
  to?: string;
}

/** `?from&to` of a report page: kept only when both are dates. */
export const periodSearch = (search: Record<string, unknown>): PeriodSearch => {
  const day = (v: unknown) => (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : undefined);
  const from = day(search.from);
  const to = day(search.to);
  return from && to ? { from, to } : {};
};
